import { columnAt, keyTarget } from './chartMath.js'
import { seriesColor } from './colors.js'

// The season views' charts: hand-built SVG, no library. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// EVERY MARK IS AN SVG ELEMENT IN A 100 BY 100 BOX, EVERY LABEL IS TEXT OUTSIDE IT
// ═══════════════════════════════════════════════════════════════════════════
//
// A chart here is a `viewBox="0 0 100 100"` stretched over its container (`preserveAspectRatio="none"`), so the bars
// and the line fill whatever width the page gives them with no measuring and no resize observer. Text cannot live in
// that box (it would be stretched with it), so the axis labels, the month names and the readout are HTML beside it.
// Strokes are `vector-effect: non-scaling-stroke`, so a line is one pixel thick however the box is stretched, and the
// end dots are HTML spans positioned by percentage, because a circle in a stretched box is an ellipse.
//
// NO LIBRARY, AND NONE WAS NEEDED. The brief says to stop and say why if one seems necessary; the design's own
// charts (bars, a line, an area, a dotted reference) are paths and rectangles, and `chartMath.js` is the arithmetic.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE READOUT IS ALWAYS FILLED, AND EVERY CHART IS A SLIDER
// ═══════════════════════════════════════════════════════════════════════════
//
// There is no tooltip to hover for: a game's number is printed above its chart at all times, and the chart opens on the
// season high (the trend) or the last game (a trajectory). Dragging across the chart, hovering it, the ‹ › buttons and
// the arrow keys move the readout. The plot is `role="slider"` with `aria-valuemin`, `aria-valuemax`, `aria-valuenow`
// and a `aria-valuetext` that says the game in words, so a keyboard or a screen reader gets what a finger gets.
// `chartMath.js::columnAt` and `keyTarget` are the two functions that decide where a pointer or a key goes, and are
// tested as numbers; a pointer needs a layout, which jsdom does not have, so the real-browser case in
// `viewport.test.js` drives it in Chromium.
//
// ═══════════════════════════════════════════════════════════════════════════
// COLOUR IS A CUSTOM PROPERTY, NOT AN ATTRIBUTE AND NOT A LITERAL
// ═══════════════════════════════════════════════════════════════════════════
//
// A series takes its colour from `--hoops-line` (a line, an area, an end dot) or `--hoops-bar` (one bar), set inline from
// the team's primary (`colors.js::seriesColor`), and the stylesheet decides what to do with it: a loss is the same colour
// at a lower alpha, an area is the same colour at a lower alpha again, and a reference mark (the season average, .500, the
// zero line, a gridline) is ink and never colour. A team with no usable primary sets nothing and the stylesheet's
// neutral shows, so an unknown team is drawn neutral and never as some other team.

/** A chevron drawn with two borders, as the design draws its back and step controls: `left`, `right` or `down`. */
export function Chevron({ direction }) {
  return <span className={`d2d-hoops__chevron d2d-hoops__chevron--${direction}`} aria-hidden="true" />
}

/**
 * Pointer and key handlers that move a selection over `count` columns.
 *
 * `selected` is the index in force and `onSelect(index)` is called with a new one, and never with the one already
 * selected, so a drag across one column does not re-render it forty times.
 */
export function scrubHandlers({ count, selected, onSelect }) {
  const move = (index) => {
    if (index !== null && index !== selected) onSelect(index)
  }
  return {
    onPointerDown: (event) => {
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        // Not every environment can capture a pointer, and a drag that cannot is still a series of moves.
      }
      move(columnAt(event.clientX, event.currentTarget.getBoundingClientRect(), count))
    },
    onPointerMove: (event) => {
      if (event.buttons || event.pointerType === 'mouse') {
        move(columnAt(event.clientX, event.currentTarget.getBoundingClientRect(), count))
      }
    },
    onKeyDown: (event) => {
      const target = keyTarget(event.key, selected, count)
      if (target === null) return
      event.preventDefault()
      move(target)
    },
  }
}

/** A short line with a dot at its end: a leader's last ten games, or one team's season in a row. */
export function Sparkline({ spark, testId }) {
  if (spark === null || spark === undefined || spark.empty) return null
  return (
    <span className="d2d-hoops__spark" aria-hidden="true" data-testid={testId}>
      <svg viewBox={spark.box} preserveAspectRatio="none" focusable="false">
        {spark.base === '' ? null : <path className="d2d-hoops__sparkbase" d={spark.base} />}
        <path className="d2d-hoops__sparkpath" d={spark.d} />
      </svg>
      {spark.dot === null ? null : (
        <span className="d2d-hoops__sparkdot" style={{ left: `${spark.dot.left}%`, top: `${spark.dot.top}%` }} />
      )}
    </span>
  )
}

/** One team's season as games over .500, small, on the shared scale: the standings' row line and the teams' tile line. */
export function MiniTrajectory({ mini, testId }) {
  if (mini === null || mini === undefined || mini.empty) return null
  return (
    <span className="d2d-hoops__spark d2d-hoops__spark--mini" aria-hidden="true" data-testid={testId}>
      <svg viewBox={mini.box} preserveAspectRatio="none" focusable="false">
        <path className="d2d-hoops__sparkbase" d={mini.zero} />
        <path className="d2d-hoops__sparkpath" d={mini.d} />
      </svg>
      {mini.dot === null ? null : (
        <span className="d2d-hoops__sparkdot" style={{ left: `${mini.dot.left}%`, top: `${mini.dot.top}%` }} />
      )}
    </span>
  )
}

/** The y-axis labels, in a column beside the plot. */
function AxisLabels({ ticks }) {
  return (
    <div className="d2d-hoops__axis" aria-hidden="true">
      {ticks.map((tick) => (
        <span key={tick.label + tick.top} className="d2d-hoops__axislabel" style={{ top: `${tick.top}%` }}>
          {tick.label}
        </span>
      ))}
    </div>
  )
}

/** The month names under a plot, each at the place its first game is. */
function MonthRow({ months }) {
  return (
    <div className="d2d-hoops__months" aria-hidden="true">
      {months.map((month) => (
        <span key={`${month.label}-${month.left}`} className="d2d-hoops__month" style={{ left: `${month.left}%` }}>
          {month.label}
        </span>
      ))}
    </div>
  )
}

/**
 * The game-by-game bars for one player and stat, with the picker, the readout and the legend.
 *
 * `model` is `playerModel.js::trendModel`'s answer; `picks` is the stats the picker offers (`{ key, label }`),
 * `onStat(key)` and `onSelect(index)` report a choice, `teams` is `{ byAbbr }` from `historyStore.useTeams`, and
 * `testId` prefixes the hooks the tests use.
 */
export function TrendChart({ model, picks, onStat, onSelect, teams }) {
  const scrub = scrubHandlers({ count: model.count, selected: model.selected, onSelect })
  const slot = 100 / model.count
  const colorFor = (abbr) => seriesColor(teams.byAbbr(abbr))
  const legendColor = colorFor(model.lastTeam)
  const legendStyle = legendColor === null ? undefined : { '--hoops-bar': legendColor }

  return (
    <section className="d2d-hoops__panel" aria-label="Game by game" data-testid="hoops-trend">
      <div className="d2d-hoops__panelhead">
        <span className="d2d-hoops__paneltitle d2d-micro">Game by game</span>
        <span className="d2d-hoops__panelnote d2d-micro" data-testid="hoops-trend-count">
          {model.countLine}
        </span>
      </div>

      <div className="d2d-hoops__picks" role="group" aria-label="Stat">
        {picks.map((pick) => (
          <button
            type="button"
            key={pick.key}
            className="d2d-hoops__pick"
            aria-pressed={pick.key === model.key ? 'true' : 'false'}
            data-testid={`hoops-trend-pick-${pick.key}`}
            onClick={() => onStat(pick.key)}
          >
            {pick.label}
          </button>
        ))}
      </div>

      <div className="d2d-hoops__scrubrow">
        <button
          type="button"
          className="d2d-hoops__step"
          aria-label="Previous game"
          data-testid="hoops-trend-prev"
          onClick={() => model.selected > 0 && onSelect(model.selected - 1)}
        >
          <Chevron direction="left" />
        </button>
        <div className="d2d-hoops__readout" aria-live="polite" data-testid="hoops-trend-readout">
          <div className="d2d-hoops__readkicker d2d-micro">{model.readKicker}</div>
          <div className="d2d-hoops__readvalue">
            <span className="d2d-hoops__readnumber">{model.readValue}</span>
            <span className="d2d-hoops__readunit d2d-micro">{model.readUnit}</span>
          </div>
          <div className="d2d-hoops__readsub">{model.readSub}</div>
          <div className="d2d-hoops__readline">{model.readLine}</div>
        </div>
        <button
          type="button"
          className="d2d-hoops__step"
          aria-label="Next game"
          data-testid="hoops-trend-next"
          onClick={() => model.selected < model.count - 1 && onSelect(model.selected + 1)}
        >
          <Chevron direction="right" />
        </button>
      </div>

      <div className="d2d-hoops__plotgrid d2d-hoops__plotgrid--trend">
        <AxisLabels ticks={model.ticks} />
        <div
          className="d2d-hoops__plot"
          role="slider"
          tabIndex={0}
          aria-label={model.label}
          aria-valuemin={1}
          aria-valuemax={model.count}
          aria-valuenow={model.selected + 1}
          aria-valuetext={model.valueText}
          data-testid="hoops-trend-plot"
          {...scrub}
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true">
            {model.ticks.map((tick) => (
              <line key={tick.label + tick.top} className="d2d-hoops__grid" x1="0" x2="100" y1={tick.top} y2={tick.top} />
            ))}
            {model.bars.map((bar) => {
              const color = colorFor(bar.team)
              return (
                <rect
                  key={bar.index}
                  className="d2d-hoops__bar2"
                  data-win={bar.win ? 'true' : 'false'}
                  data-selected={bar.selected ? 'true' : 'false'}
                  data-testid="hoops-trend-bar"
                  x={bar.x + slot * 0.14}
                  width={slot * 0.72}
                  y={bar.top}
                  height={bar.height}
                  style={color === null ? undefined : { '--hoops-bar': color }}
                />
              )
            })}
            <line className="d2d-hoops__zero" x1="0" x2="100" y1={model.zero} y2={model.zero} />
            {model.average === null ? null : (
              <line className="d2d-hoops__avgline" x1="0" x2="100" y1={model.average} y2={model.average} />
            )}
          </svg>
        </div>
        <span />
        <MonthRow months={model.months} />
      </div>

      <div className="d2d-hoops__legend d2d-micro">
        <span className="d2d-hoops__legenditem">
          <span className="d2d-hoops__swatch" style={legendStyle} data-win="true" />
          Win
        </span>
        <span className="d2d-hoops__legenditem">
          <span className="d2d-hoops__swatch" style={legendStyle} data-win="false" />
          Loss
        </span>
        <span className="d2d-hoops__legenditem">
          <span className="d2d-hoops__swatch d2d-hoops__swatch--dash" />
          {model.averageLabel}
        </span>
      </div>
    </section>
  )
}

/**
 * A team's season as games over .500: the readout above, the area and line, the axis, the months, and (optionally) a
 * caption or an action beside it. `model` is `teamModel.js::trajectoryModel`'s answer and `color` is the team's chart
 * colour from `colors.js::seriesColor` (or `null`). `plotHeight` is `compact` (a standings row opened in place) or
 * `full` (the team page).
 */
export function TrajectoryChart({ model, color, onSelect, plotHeight = 'full', testId = 'hoops-traj', children }) {
  const scrub = scrubHandlers({ count: model.count, selected: model.selected, onSelect })
  const style = color === null ? undefined : { '--hoops-line': color }
  return (
    <div className="d2d-hoops__traj" style={style} data-testid={testId}>
      <div className="d2d-hoops__trajread">
        <span className="d2d-hoops__readkicker d2d-micro">{model.kicker}</span>
        <span className="d2d-hoops__trajvalue" aria-live="polite" data-testid={`${testId}-read`}>
          {model.read}
        </span>
      </div>
      <div className={`d2d-hoops__plotgrid d2d-hoops__plotgrid--${plotHeight}`}>
        <AxisLabels ticks={model.ticks} />
        <div
          className="d2d-hoops__plot"
          role="slider"
          tabIndex={0}
          aria-label={model.label}
          aria-valuemin={1}
          aria-valuemax={model.count}
          aria-valuenow={model.selected + 1}
          aria-valuetext={model.read}
          data-testid={`${testId}-plot`}
          {...scrub}
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true">
            {model.ticks.map((tick) => (
              <line
                key={tick.label + tick.top}
                className={tick.zero ? 'd2d-hoops__zero' : 'd2d-hoops__grid'}
                x1="0"
                x2="100"
                y1={tick.top}
                y2={tick.top}
              />
            ))}
            <path className="d2d-hoops__area" d={model.area} />
            <path className="d2d-hoops__linepath" d={model.d} />
          </svg>
          <span className="d2d-hoops__selline" style={{ left: `${model.selectedLeft}%` }} />
          <span
            className="d2d-hoops__seldot"
            style={{ left: `${model.selectedLeft}%`, top: `${model.selectedTop}%` }}
          />
        </div>
        <span />
        <MonthRow months={model.months} />
      </div>
      <div className="d2d-hoops__trajfoot">
        <span className="d2d-hoops__trajcaption" data-testid={`${testId}-caption`}>
          {model.caption}
        </span>
        {children}
      </div>
    </div>
  )
}
